class ProductZoom extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.dialog = this.querySelector('[data-ref="zoom-dialog"]');
    this.gallery = this.closest('product-media-gallery');
    if (!this.dialog) return;
    this.scale = 1;
    this.pan = { x: 0, y: 0 };
    this.pointers = new Map();
    const options = { signal: this.controller.signal };
    const sliderEl = this.querySelector('[data-ref="zoom-slider"]');
    const multiple = this.querySelectorAll('.product-zoom__slide').length > 1;
    if (sliderEl && typeof Splide !== 'undefined') {
      this.slider = new Splide(sliderEl, { type: 'slide', perPage: 1, rewind: multiple, gap: 0, pagination: multiple && this.dialog.hasAttribute('data-pagination'), arrows: multiple, drag: multiple, keyboard: false, speed: matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 400 });
      const thumbs = this.querySelector('[data-ref="zoom-thumbnails"]');
      if (thumbs && multiple) {
        this.thumbnails = new Splide(thumbs, { fixedWidth: 70, fixedHeight: 70, gap: 8, pagination: false, isNavigation: true, arrows: false, drag: 'free', breakpoints: { 750: { fixedWidth: 50, fixedHeight: 50 } } });
        this.slider.sync(this.thumbnails);
      }
      this.slider.on('move', () => this.reset());
      this.slider.mount();
      this.thumbnails?.mount();
    }
    this.gallery?.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-zoom-trigger]');
      if (!trigger || trigger.closest('dialog')) return;
      event.preventDefault();
      this.open(trigger);
    }, options);
    this.gallery?.addEventListener('keydown', (event) => {
      const trigger = event.target.closest('[data-zoom-trigger]');
      if (trigger && !trigger.closest('dialog') && ['Enter', ' '].includes(event.key)) { event.preventDefault(); this.open(trigger); }
    }, options);
    this.querySelector('[data-ref="zoom-close"]')?.addEventListener('click', () => this.dialog.close(), options);
    this.dialog.addEventListener('close', () => {
      this.reset();
      if (!document.querySelector('dialog[open]')) document.body.classList.remove('overflow-hidden');
      this.trigger?.focus({ preventScroll: true });
    }, options);
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    }, options);
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.slider?.go('<'); }
      if (event.key === 'ArrowRight') { event.preventDefault(); this.slider?.go('>'); }
      if (['+', '='].includes(event.key)) { event.preventDefault(); this.setScale(this.scale + .5); }
      if (event.key === '-') { event.preventDefault(); this.setScale(this.scale - .5); }
      if (event.key === '0') this.reset();
    }, options);
    this.dialog.addEventListener('wheel', (event) => {
      if (!event.target.matches('.product-zoom__image')) return;
      event.preventDefault();
      this.setScale(this.scale + (event.deltaY < 0 ? .25 : -.25));
    }, { ...options, passive: false });
    this.dialog.addEventListener('dblclick', (event) => {
      if (event.target.matches('.product-zoom__image')) { this.setScale(this.scale > 1 ? 1 : 2.5); event.preventDefault(); }
    }, options);
    this.dialog.addEventListener('pointerdown', (event) => {
      if (!event.target.matches('.product-zoom__image')) return;
      this.image = event.target;
      if (this.scale > 1 || this.pointers.size > 0) event.preventDefault();
      event.target.setPointerCapture(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.distance = this.getDistance();
      this.baseScale = this.scale;
      this.origin = { x: event.clientX, y: event.clientY, panX: this.pan.x, panY: this.pan.y };
    }, options);
    this.dialog.addEventListener('pointermove', (event) => {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size === 2 && this.distance) {
        event.preventDefault(); this.setScale(this.baseScale * this.getDistance() / this.distance);
      } else if (this.scale > 1 && this.origin) {
        event.preventDefault();
        this.pan = { x: this.origin.panX + event.clientX - this.origin.x, y: this.origin.panY + event.clientY - this.origin.y };
        this.transform();
      }
    }, options);
    const release = (event) => {
      this.pointers.delete(event.pointerId);
      const point = this.pointers.values().next().value;
      if (point) this.origin = { ...point, panX: this.pan.x, panY: this.pan.y };
      this.distance = this.getDistance(); this.baseScale = this.scale;
    };
    this.dialog.addEventListener('pointerup', release, options);
    this.dialog.addEventListener('pointercancel', release, options);
    // Splide écoute touchstart/touchmove sur sa piste : un pincement ou une image agrandie ne doit pas devenir un balayage qui change d'image.
    for (const type of ['touchstart', 'touchmove']) {
      this.dialog.addEventListener(type, (event) => {
        if (event.target.matches('.product-zoom__image') && (event.touches.length > 1 || this.scale > 1)) event.stopPropagation();
      }, { ...options, capture: true, passive: true });
    }
    const controls = document.createElement('div');
    controls.className = 'cr-zoom-controls';
    for (const [key, text, action] of [['zoomInLabel', '+', () => this.setScale(this.scale + .5)], ['zoomOutLabel', '−', () => this.setScale(this.scale - .5)], ['resetZoomLabel', '1:1', () => this.reset()]]) {
      const button = document.createElement('button'); button.type = 'button'; button.textContent = text;
      button.setAttribute('aria-label', this.gallery?.dataset[key] || text);
      button.addEventListener('click', action, options); controls.append(button);
    }
    this.dialog.append(controls);
    this.controls = controls;
  }
  disconnectedCallback() {
    this.controller?.abort(); this.slider?.destroy(true); this.thumbnails?.destroy(true); this.controls?.remove();
    if (!document.querySelector('dialog[open]')) document.body.classList.remove('overflow-hidden');
  }
  // index facultatif : ouverture à une photo précise (bouton « Voir plus de photos »), le focus revient ensuite au déclencheur.
  open(trigger, index = null) {
    this.trigger = trigger; this.reset();
    if (index === null) index = [...this.querySelectorAll('.product-zoom__slide')].findIndex((slide) => slide.dataset.mediaId === trigger.dataset.zoomMediaId);
    if (index >= 0) this.slider?.go(index);
    this.dialog.showModal(); document.body.classList.add('overflow-hidden');
    requestAnimationFrame(() => { this.slider?.refresh(); this.thumbnails?.refresh(); this.querySelector('[data-ref="zoom-close"]')?.focus(); });
  }
  getDistance() {
    const points = [...this.pointers.values()];
    return points.length === 2 ? Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y) : 0;
  }
  setScale(scale) {
    this.scale = Math.max(1, Math.min(4, scale));
    if (this.scale === 1) this.pan = { x: 0, y: 0 };
    if (this.slider) this.slider.options = { drag: this.scale === 1 && this.querySelectorAll('.product-zoom__slide').length > 1 };
    this.transform();
  }
  transform() {
    const image = this.querySelector('.product-zoom__slide.is-active .product-zoom__image') || this.image || this.querySelector('.product-zoom__image');
    if (!image) return;
    const limitX = image.clientWidth * (this.scale - 1) / 2;
    const limitY = image.clientHeight * (this.scale - 1) / 2;
    this.pan.x = Math.max(-limitX, Math.min(limitX, this.pan.x));
    this.pan.y = Math.max(-limitY, Math.min(limitY, this.pan.y));
    image.style.transform = `translate(${this.pan.x}px, ${this.pan.y}px) scale(${this.scale})`;
    image.style.cursor = this.scale > 1 ? 'grab' : 'zoom-in';
  }
  reset() {
    this.scale = 1; this.pan = { x: 0, y: 0 }; this.pointers?.clear();
    this.querySelectorAll('.product-zoom__image').forEach((image) => { image.style.transform = ''; });
    if (this.slider) this.slider.options = { drag: this.querySelectorAll('.product-zoom__slide').length > 1 };
  }
}
if (!customElements.get('product-zoom')) customElements.define('product-zoom', ProductZoom);
